import type { AnnouncementPopupOptionsInput } from '../../../lib/announcements';

/**
 * 「팝업으로도 알리기」 옵션의 값 목록·화면 문구·기본값 — 결정
 * `docs/decisions/2026-10-26-popup-close-is-a-session-and-opt-out-is-an-option.md`의 ⓐ 표·개정 Q4.
 *
 * ⚠️ **`lib/announcements.ts`에 두지 않는다** — 그 모듈을 `vi.mock` 팩토리로 대체하는 spec이 있어
 * (`pages/settings/__tests__/announcements.test.tsx`) 런타임 값을 더하면 그 spec 전체가
 * `No "X" export is defined on the mock`으로 죽는다. 타입만 그쪽에서 가져온다.
 *
 * ⚠️ 두 `Record`가 **exhaustive**인 것이 래칫이다 — 서버 값이 늘어 `AnnouncementPopupOptionsInput`을
 * 넓히면 여기서 tsc가 깨진다(문구 없는 선택지가 조용히 생기지 않는다).
 */

type PopupFrequency = AnnouncementPopupOptionsInput['frequency'];
type PopupOptOut = AnnouncementPopupOptionsInput['optOut'];

export const POPUP_FREQUENCY_LABELS: Record<PopupFrequency, string> = {
  ONCE: '한 번 — 읽으면 다시 뜨지 않음',
  EVERY_VISIT: '매번 — 접속할 때마다 다시 뜸',
};

export const POPUP_OPT_OUT_LABELS: Record<PopupOptOut, string> = {
  NONE: '없음',
  HIDE_TODAY: '「오늘 하루 보지 않기」',
  NEVER_AGAIN: '「다시 보지 않기」',
};

/** 화면에 늘어놓는 순서 — 위 `Record`의 키와 같은 집합이다 */
export const POPUP_FREQUENCIES = Object.keys(POPUP_FREQUENCY_LABELS) as PopupFrequency[];
export const POPUP_OPT_OUTS = Object.keys(POPUP_OPT_OUT_LABELS) as PopupOptOut[];

/**
 * 켤 때 기본값 — 「가벼운 공지」(`ONCE` + 「오늘 하루」). 서버 기본값
 * (`DEFAULT_ANNOUNCEMENT_POPUP_*`)과 **같은 값**이고, 화면이 그 값을 미리 골라 둔 상태로 보여 준다.
 */
export const DEFAULT_POPUP_OPTIONS: AnnouncementPopupOptionsInput = {
  frequency: 'ONCE',
  optOut: 'HIDE_TODAY',
};

/** 저장된 값(열린 와이어 문자열)을 폼 값으로 — 모르는 값이면 기본값으로 떨어뜨린다 */
export function toPopupOptions(
  stored: { frequency: string; optOut: string } | null | undefined,
): AnnouncementPopupOptionsInput {
  return {
    frequency: (POPUP_FREQUENCIES as string[]).includes(stored?.frequency ?? '')
      ? (stored!.frequency as PopupFrequency)
      : DEFAULT_POPUP_OPTIONS.frequency,
    optOut: (POPUP_OPT_OUTS as string[]).includes(stored?.optOut ?? '')
      ? (stored!.optOut as PopupOptOut)
      : DEFAULT_POPUP_OPTIONS.optOut,
  };
}
